import { useRef, useEffect, useState, useMemo } from "react";
import { API, api, EXTRACT_LABELS } from "@/lib/api";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import {
  ArrowLeft, CheckCircle2, Clock, Mail, MailOpen, AlertCircle, ChevronDown, Trash2, Loader2, RotateCcw,
  FileText, Code2, Paperclip, Sparkles, Reply, ReplyAll, Forward, Landmark, Smile, Minimize2, Send, Save, ClipboardList, PenLine, X,
} from "lucide-react";
import { PRIORITIES, fmtDate, buildHLRegex } from "./inboxUtils";

// Evidenzia i termini cercati nel testo semplice (React nodes, XSS-safe).
const HLText = ({ text, re }) => {
  const s = text == null ? "" : String(text);
  if (!re || !s) return s || null;
  return s.split(re).map((p, i) => (i % 2 === 1
    ? <mark key={i} className="bg-yellow-200 text-inherit font-semibold rounded-[2px] px-0.5">{p}</mark>
    : p));
};

// Evidenzia i termini cercati DENTRO l'HTML dell'email (iframe): cammina i nodi di testo e avvolge i
// match in <mark>, senza toccare tag/attributi. Ricostruisce il documento preservando <head> (stili).
const highlightHtmlString = (html, query) => {
  const re = buildHLRegex(query);
  if (!html || !re) return html;
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
    const targets = [];
    let n;
    while ((n = walker.nextNode())) {
      const pn = n.parentNode ? n.parentNode.nodeName.toLowerCase() : "";
      if (pn === "script" || pn === "style" || pn === "mark") continue;
      if (n.nodeValue && n.nodeValue.trim()) targets.push(n);
    }
    targets.forEach((node) => {
      const parts = node.nodeValue.split(re);
      if (parts.length < 2) return;
      const frag = doc.createDocumentFragment();
      parts.forEach((part, i) => {
        if (part === "") return;
        if (i % 2 === 1) {
          const m = doc.createElement("mark");
          m.setAttribute("style", "background:#fef08a;color:inherit;border-radius:2px;padding:0 1px");
          m.textContent = part;
          frag.appendChild(m);
        } else {
          frag.appendChild(doc.createTextNode(part));
        }
      });
      node.parentNode.replaceChild(frag, node);
    });
    const head = doc.head ? doc.head.innerHTML : "";
    return `<!doctype html><html><head>${head}</head><body>${doc.body.innerHTML}</body></html>`;
  } catch { return html; }
};

// Evidenzia i termini cercati nello snippet dell'allegato (senza innerHTML, XSS-safe).
const highlightSnippet = (snippet, terms) => {
  if (!snippet) return null;
  if (!terms || !terms.length) return snippet;
  const esc = terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).filter(Boolean);
  if (!esc.length) return snippet;
  const re = new RegExp(`(${esc.join("|")})`, "gi");
  return snippet.split(re).map((part, i) =>
    esc.some((t) => new RegExp(`^${t}$`, "i").test(part))
      ? <mark key={i} className="bg-amber-300/70 text-amber-950 rounded px-0.5 font-semibold">{part}</mark>
      : <span key={i}>{part}</span>
  );
};

const escapeHtml = (s) => (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const textToHtml = (t) => escapeHtml(t).replace(/\n/g, "<br>");
const sigWrap = (html) => html ? `<div data-signature="true" data-testid="draft-signature-block" style="margin-top:14px">${DOMPurify.sanitize(html)}</div>` : "";

export const EmailDetail = ({
  selected, isInbox, isTrash, busy, detailLoading, showHtml, htmlHeight, replyAll, draft, rewritingReply,
  setSelected, setShowHtml, setHtmlHeight, setReplyAll, setDraft,
  onToggleRead, onChangePriority, onDeleteMail, onRestoreMail, onRewriteDraft, onApprove, onSaveDraft, onOpenPratica,
  onReply, onReplyAll, onForward,
  catStyle, catLabel, appliedDraftTone, signatureHtml, highlight,
}) => {
  const draftEditorRef = useRef(null);
  const scrollRef = useRef(null);
  const replyFileRef = useRef(null);
  const [replyAtts, setReplyAtts] = useState([]);
  const [uploadingAtt, setUploadingAtt] = useState(false);
  const hlRe = useMemo(() => buildHLRegex(highlight), [highlight]);

  // Reset allegati risposta quando cambia l'email selezionata
  useEffect(() => {
    setReplyAtts((prev) => { prev.forEach((a) => a.previewUrl && URL.revokeObjectURL(a.previewUrl)); return []; });
  }, [selected?.id]);

  const fmtSize = (n) => {
    if (n == null) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  };

  const onAttachReply = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploadingAtt(true);
    for (const f of files) {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: troppo grande (max 10 MB)`); continue; }
      const previewUrl = f.type.startsWith("image/") ? URL.createObjectURL(f) : null;
      try {
        const fd = new FormData(); fd.append("file", f);
        const r = await api.post("/attachments/upload", fd);
        setReplyAtts((prev) => [...prev, { id: r.data.id, filename: r.data.filename, size: f.size, contentType: f.type, previewUrl }]);
      } catch {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        toast.error(`Caricamento di ${f.name} non riuscito`);
      }
    }
    setUploadingAtt(false);
  };
  const removeReplyAtt = (id) => setReplyAtts((prev) => {
    const t = prev.find((a) => a.id === id);
    if (t?.previewUrl) URL.revokeObjectURL(t.previewUrl);
    return prev.filter((a) => a.id !== id);
  });

  // Ogni volta che si apre un'email, riparti sempre dall'inizio (fix scroll su mobile).
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [selected?.id]);

  // Inizializza/riallinea l'editor della bozza AI (corpo + firma con logo) quando cambia l'email o la firma.
  useEffect(() => {
    if (!isInbox || !selected) return;
    const el = draftEditorRef.current;
    if (!el) return;
    el.innerHTML = `<div data-body="true">${textToHtml(selected.draft_reply || "") || "<br>"}</div>${sigWrap(signatureHtml)}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, signatureHtml, isInbox]);

  const getBodyText = () => {
    const el = draftEditorRef.current;
    if (!el) return "";
    const b = el.querySelector('[data-body="true"]');
    return (b ? b.innerText : el.innerText).trim();
  };
  const getFullHtml = () => draftEditorRef.current?.innerHTML || "";
  const setEditorBody = (text) => {
    const el = draftEditorRef.current;
    if (!el) return;
    const b = el.querySelector('[data-body="true"]');
    if (b) { b.innerHTML = textToHtml(text); }
    else { el.innerHTML = `<div data-body="true">${textToHtml(text)}</div>${sigWrap(signatureHtml)}`; }
  };
  const doRewrite = async (tone) => {
    const nt = await onRewriteDraft(tone, getBodyText());
    if (nt != null) setEditorBody(nt);
  };
  const doApprove = () => onApprove({ text: getBodyText(), html: getFullHtml(), attachment_ids: replyAtts.map((a) => a.id) });
  const doSaveDraft = () => onSaveDraft({ text: getBodyText(), html: getFullHtml() });

  return (
  <div ref={scrollRef} className={`flex-1 h-full overflow-y-auto scroll-thin bg-white ${selected ? "block" : "hidden md:block"}`}>
    {!selected ? (
      <div className="h-full hidden md:flex items-center justify-center text-gray-400">Seleziona un'email</div>
    ) : (
      <div className="fade-in">
        <div className="px-4 sm:px-8 pt-6 pb-4 border-b border-gray-200 bg-white/80 backdrop-blur-xl sticky top-0 z-10">
          <button data-testid="detail-back-button" onClick={() => setSelected(null)} className="md:hidden inline-flex items-center gap-1.5 text-sm text-[#007BFF] mb-3">
            <ArrowLeft className="h-4 w-4" />Indietro
          </button>
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-4">
            <div className="flex items-center gap-2 flex-wrap justify-end order-1 md:order-2 md:shrink-0">
              {isInbox && (selected.is_pratica || selected.pratica_id) && (
                <Button data-testid="open-pratica-button" onClick={onOpenPratica} disabled={busy} size="sm"
                  className="rounded-md bg-[#007BFF] hover:bg-[#0056B3] text-white">
                  <ClipboardList className="h-4 w-4 mr-1.5" />{selected.pratica_id ? "Vedi Pratica AI" : "Apri Pratica AI"}
                </Button>
              )}
              {isInbox && (
                <Button data-testid="toggle-read-button" onClick={onToggleRead} disabled={busy} variant="outline" size="sm"
                  aria-label={selected.is_read ? "Segna come da leggere" : "Segna come letta"} title={selected.is_read ? "Segna come da leggere" : "Segna come letta"}
                  className="rounded-md">
                  {selected.is_read ? <Mail className="h-4 w-4" /> : <MailOpen className="h-4 w-4" />}
                </Button>
              )}
              {isInbox && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid="priority-selector" className="inline-flex items-center gap-1.5 border border-gray-200 rounded-md px-3 py-1.5 text-sm font-medium hover:bg-gray-50 transition-colors duration-150">
                      <AlertCircle className="h-4 w-4" />
                      <span className="capitalize">{selected.priority || "media"}</span>
                      <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {PRIORITIES.map((p) => (
                      <DropdownMenuItem key={p.key} data-testid={`priority-option-${p.key}`} onClick={() => onChangePriority(p.key)}>
                        <span className={`h-2.5 w-2.5 rounded-full mr-2 ${p.key === "alta" ? "bg-red-500" : p.key === "media" ? "bg-amber-400" : "bg-slate-300"}`} />{p.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {isTrash && (
                <Button data-testid="restore-email-button" onClick={onRestoreMail} disabled={busy} variant="outline" size="sm"
                  className="rounded-md border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800">
                  <RotateCcw className="h-4 w-4 mr-1.5" />Ripristina
                </Button>
              )}
              <Button data-testid="delete-email-button" onClick={onDeleteMail} disabled={busy} variant="outline" size="sm" aria-label="Elimina email"
                className="rounded-md border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <div className="min-w-0 order-2 md:order-1">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-md border ${catStyle(selected.category)}`}>{catLabel(selected.category)}</span>
                {selected.draft_approved && <span className="text-[10px] uppercase text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Risposto</span>}
              </div>
              <h2 className="font-head text-2xl font-bold tracking-tight break-words">{selected.subject}</h2>
              <div className="mt-1.5 space-y-0.5">
                <p data-testid="detail-from" className="text-sm text-gray-500 break-words [overflow-wrap:anywhere]">Da: <span className="text-gray-900 font-medium">{selected.sender_name}</span></p>
                {selected.recipient && <p data-testid="detail-to" className="text-xs text-gray-400 break-words [overflow-wrap:anywhere]">A: {selected.recipient}</p>}
                {selected.cc && String(selected.cc).includes("@") && <p data-testid="detail-cc" className="text-xs text-gray-400 break-words [overflow-wrap:anywhere]">Cc: {selected.cc}</p>}
                {selected.bcc && String(selected.bcc).includes("@") && <p data-testid="detail-bcc" className="text-xs text-gray-400 break-words [overflow-wrap:anywhere]">Ccn: {selected.bcc}</p>}
                <p data-testid="detail-date" className="text-xs text-gray-400 flex items-center gap-1"><Clock className="h-3 w-3" />{fmtDate(selected.email_date || selected.created_at)}</p>
              </div>
            </div>
          </div>
        </div>

        {!isInbox && (
          <div className="px-4 sm:px-8 py-3 border-b border-gray-100 flex flex-wrap items-center gap-2" data-testid="sent-actions-bar">
            <Button data-testid="detail-reply-btn" onClick={() => onReply?.(selected)} variant="outline" size="sm" className="rounded-md">
              <Reply className="h-4 w-4 mr-1.5" />Rispondi
            </Button>
            <Button data-testid="detail-replyall-btn" onClick={() => onReplyAll?.(selected)} variant="outline" size="sm" className="rounded-md">
              <ReplyAll className="h-4 w-4 mr-1.5" />Rispondi a tutti
            </Button>
            <Button data-testid="detail-forward-btn" onClick={() => onForward?.(selected)} variant="outline" size="sm" className="rounded-md">
              <Forward className="h-4 w-4 mr-1.5" />Inoltra
            </Button>
          </div>
        )}

        <div className="p-4 sm:p-8 space-y-6">
          {detailLoading && (
            <div data-testid="analyzing-banner" className="flex items-center gap-2 text-sm text-[#007BFF] bg-[#007BFF]/5 border border-[#007BFF]/20 rounded-md px-4 py-2.5">
              <Loader2 className="h-4 w-4 animate-spin" />Analisi AI in corso…
            </div>
          )}

          {selected.attachment_match && (
            <div data-testid="attachment-match-box" className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-xs font-semibold text-amber-700 mb-2">
                <Paperclip className="h-3.5 w-3.5" />
                Trovato nell'allegato: <span className="font-bold underline decoration-amber-400" data-testid="attachment-match-filename">{selected.attachment_match.filename}</span>
              </div>
              <p className="text-sm leading-relaxed text-amber-900/90" data-testid="attachment-match-snippet">
                {highlightSnippet(selected.attachment_match.snippet, selected.attachment_match.terms)}
              </p>
            </div>
          )}

          <div data-testid="email-content-section">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-[0.18em] font-semibold text-gray-500 flex items-center gap-2"><FileText className="h-3.5 w-3.5" />Contenuto</div>
              {selected.body_html && (
                <button data-testid="toggle-html-button" onClick={() => setShowHtml((v) => !v)} className="text-xs flex items-center gap-1 text-gray-500 hover:text-gray-900 transition-colors duration-150">
                  {showHtml ? <><FileText className="h-3.5 w-3.5" />Vedi testo</> : <><Code2 className="h-3.5 w-3.5" />Vedi HTML</>}
                </button>
              )}
            </div>
            {selected.body_html && showHtml ? (
              <iframe title="email-html" data-testid="email-html-frame" sandbox="allow-same-origin" srcDoc={hlRe ? highlightHtmlString(selected.body_html, highlight) : selected.body_html}
                onLoad={(e) => {
                  try {
                    const b = e.target.contentDocument?.body;
                    const h = b ? b.scrollHeight : 180;
                    setHtmlHeight(Math.min(Math.max(h + 24, 80), Math.round(window.innerHeight * 0.75)));
                  } catch { setHtmlHeight(400); }
                }}
                style={{ height: htmlHeight }}
                className="w-full bg-white" />
            ) : (
              <p className="text-sm whitespace-pre-wrap leading-relaxed text-gray-800" data-testid="email-body-text"><HLText text={selected.body || (detailLoading ? "Caricamento…" : "(nessun contenuto testuale)")} re={hlRe} /></p>
            )}
          </div>

          {selected.attachments?.length > 0 && (
            <div data-testid="attachments-list">
              <div className="text-xs uppercase tracking-[0.18em] font-semibold text-gray-500 mb-3 flex items-center gap-2"><Paperclip className="h-3.5 w-3.5" />Allegati ({selected.attachments.length})</div>
              <div className="flex flex-wrap gap-3">
                {selected.attachments.map((a, idx) => {
                  const att = typeof a === "string" ? { filename: a } : a;
                  const href = att.file_id ? `${API}/attachments/${att.file_id}/download` : null;
                  const openAtt = (e) => {
                    if (e) { e.preventDefault(); e.stopPropagation(); }
                    if (!att.file_id) { toast.info("Allegato non disponibile per l'apertura (email importata prima dell'aggiornamento)."); return; }
                    const name = att.filename || "allegato";
                    const url = `${API}/attachments/${att.file_id}/download`;
                    const viewable = /\.(pdf|png|jpe?g|gif|webp|bmp|svg|txt)$/i.test(name);
                    // Apertura SINCRONA (nel gesto utente) così i browser mobile non la bloccano come popup.
                    if (viewable) {
                      const w = window.open(url, "_blank");
                      if (!w) window.location.assign(url);
                    } else {
                      const a = document.createElement("a");
                      a.href = url; a.download = name; a.rel = "noopener";
                      document.body.appendChild(a); a.click(); a.remove();
                    }
                  };
                  return (
                    <button key={att.file_id || att.filename || idx} data-testid={`attachment-item-${idx}`}
                      type="button" onClick={openAtt} title={href ? `Apri ${att.filename} in una nuova scheda` : att.filename}
                      className={`group flex flex-col text-left border border-gray-200 rounded-md overflow-hidden bg-white w-[132px] transition-colors duration-150 ${href ? "cursor-pointer hover:border-[#007BFF]" : "cursor-default opacity-70"}`}>
                      {att.has_thumb && att.file_id ? (
                        <img src={`${API}/attachments/${att.file_id}/thumb`} alt={att.filename} loading="lazy" className="h-32 w-full object-cover object-top bg-gray-50" />
                      ) : (
                        <div className="h-32 w-full flex items-center justify-center bg-gray-50"><FileText className="h-8 w-8 text-gray-300" /></div>
                      )}
                      <span className="text-[11px] text-gray-700 px-2 py-1.5 truncate flex items-center gap-1" title={att.filename}>
                        <Paperclip className="h-3 w-3 shrink-0" />{att.filename}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {isInbox && (
            <>
              <div className="bg-blue-50/30 border border-blue-100 rounded-lg p-6">
                <div className="text-xs uppercase tracking-[0.18em] font-semibold text-gray-500 mb-3 flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#007BFF]" />Bozza di Risposta AI</div>
                <div data-testid="reply-recipients" className="text-xs text-gray-500 mb-3 space-y-0.5">
                  <div>A: <span className="font-medium text-gray-800">{selected.sender_name}</span></div>
                  {replyAll && (selected.recipient || selected.cc) && (
                    <div>Cc: <span className="font-medium text-gray-800">{[selected.recipient, selected.cc].filter(Boolean).join(", ")}</span></div>
                  )}
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <button data-testid="reply-mode-sender" onClick={() => setReplyAll(false)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors duration-150 ${!replyAll ? "bg-[#007BFF] text-white border-[#007BFF]" : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"}`}>
                    Solo al mittente
                  </button>
                  <button data-testid="reply-mode-all" onClick={() => setReplyAll(true)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors duration-150 ${replyAll ? "bg-[#007BFF] text-white border-[#007BFF]" : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"}`}>
                    <ReplyAll className="h-3.5 w-3.5 inline mr-1" />Rispondi a tutti
                  </button>
                </div>
                <div className="flex items-center gap-1 mb-2 flex-wrap">
                  <span className="text-[10px] text-gray-400">Riscrivi con AI:</span>
                  {[["istituzionale", Landmark], ["informale", Smile], ["sintetico", Minimize2]].map(([t, Icon]) => (
                    <button key={t} type="button" data-testid={`draft-rewrite-${t}`} title={`Riscrivi in tono ${t}`}
                      onClick={() => doRewrite(t)} disabled={!!rewritingReply} aria-pressed={appliedDraftTone === t}
                      className={`inline-flex items-center gap-1 h-6 px-1.5 rounded border text-[10px] font-medium disabled:opacity-50 transition-colors duration-150 capitalize ${appliedDraftTone === t ? "border-[#007BFF] bg-[#007BFF]/10 text-[#007BFF]" : "border-gray-200 text-gray-700 hover:bg-gray-100"}`}>
                      {rewritingReply === t ? <Loader2 className="h-3 w-3 animate-spin" /> : <Icon className="h-3 w-3" />}{t}
                    </button>
                  ))}
                </div>
                <div
                  ref={draftEditorRef}
                  data-testid="draft-editor"
                  contentEditable
                  suppressContentEditableWarning
                  className="w-full rounded-md text-sm leading-relaxed bg-white border border-input px-3 py-2 min-h-[160px] outline-none focus:ring-2 focus:ring-[#007BFF]/30 whitespace-pre-wrap break-words [&_img]:max-h-16 [&_[data-signature]]:mt-3 [&_[data-signature]]:pt-2 [&_[data-signature]]:border-t [&_[data-signature]]:border-dashed [&_[data-signature]]:border-gray-200 [&_a]:text-[#007BFF]"
                />
                {replyAtts.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3" data-testid="reply-attachments">
                    {replyAtts.map((a) => (
                      <span key={a.id} data-testid={`reply-att-${a.id}`} className="inline-flex items-center gap-2 text-xs bg-[#007BFF]/10 text-[#0056B3] border border-[#007BFF]/30 rounded-lg pl-1.5 pr-1.5 py-1 max-w-[240px]">
                        {a.previewUrl ? (
                          <img src={a.previewUrl} alt={a.filename} className="h-9 w-9 rounded-md object-cover shrink-0 border border-[#007BFF]/20" data-testid={`reply-att-thumb-${a.id}`} />
                        ) : (
                          <span className="h-9 w-9 rounded-md bg-[#007BFF]/15 flex items-center justify-center shrink-0"><FileText className="h-4 w-4" /></span>
                        )}
                        <span className="flex flex-col min-w-0 leading-tight">
                          <span className="truncate font-medium">{a.filename}</span>
                          {a.size != null && <span className="text-[10px] text-[#0056B3]/70" data-testid={`reply-att-size-${a.id}`}>{fmtSize(a.size)}</span>}
                        </span>
                        <button type="button" onClick={() => removeReplyAtt(a.id)} title="Rimuovi allegato" className="shrink-0 rounded-full hover:bg-[#007BFF]/20 p-0.5"><X className="h-3 w-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-3 mt-4">
                  <Button data-testid="approve-ai-draft-button" onClick={doApprove} disabled={busy || selected.draft_approved} className="bg-[#007BFF] hover:bg-[#0056B3] rounded-md">
                    <Send className="h-4 w-4 mr-2" />{selected.draft_approved ? "Inviata" : (replyAll ? "Invia a tutti" : "Approva e Invia")}
                  </Button>
                  <input ref={replyFileRef} type="file" multiple className="hidden" data-testid="reply-attach-input" onChange={onAttachReply} />
                  <Button data-testid="reply-attach-button" onClick={() => replyFileRef.current?.click()} disabled={busy || uploadingAtt || selected.draft_approved} variant="outline" className="rounded-md">
                    {uploadingAtt ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Paperclip className="h-4 w-4 mr-2" />}Allega file
                  </Button>
                  <Button data-testid="save-draft-button" onClick={doSaveDraft} disabled={busy} variant="outline" className="rounded-md">
                    <Save className="h-4 w-4 mr-2" />Salva Modifica
                  </Button>
                </div>
              </div>

              <div className="border border-[#007BFF]/30 rounded-lg p-6 bg-[#007BFF]/[0.03]">
                <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[#007BFF] mb-4 flex items-center gap-2"><Sparkles className="h-4 w-4" />Dati Estratti dall'AI</div>
                {detailLoading ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin text-[#007BFF]" />Estrazione in corso…</div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                    {Object.entries(EXTRACT_LABELS).map(([key, label]) => {
                      const vals = (selected.extracted && selected.extracted[key]) || [];
                      if (!vals.length) return null;
                      return (
                        <div key={key} data-testid={`extract-${key}`} className="flex flex-col gap-1">
                          <span className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</span>
                          <div className="flex flex-wrap gap-1.5">
                            {vals.map((v) => <span key={`${key}-${v}`} className="text-sm font-medium bg-white border border-gray-200 rounded px-2 py-0.5">{v}</span>)}
                          </div>
                        </div>
                      );
                    })}
                    {Object.values(selected.extracted || {}).every((a) => !a.length) && <p className="text-sm text-gray-500">Nessun dato strutturato rilevato.</p>}
                  </div>
                )}
              </div>

              {selected.suggested_actions?.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] font-semibold text-gray-500 mb-3">Azioni suggerite dall'AI</div>
                  <div className="flex flex-wrap gap-2">
                    {selected.suggested_actions.map((a) => (
                      <span key={a} className="text-sm bg-gray-100 border border-gray-200 rounded-md px-3 py-1.5">{a}</span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    )}
  </div>
  );
};
